import { BaseSpecialRenderer, SpecialFieldJsonData } from './base-special-renderer';
import { CaptchaRuntimeConfig, RenderOptions } from '../../models/element-types';

export interface CaptchaJsonData extends SpecialFieldJsonData {
    type: 'captcha';
    siteKey?: string;
}

export class CaptchaRenderer extends BaseSpecialRenderer<CaptchaJsonData> {
    protected override get defaultData(): CaptchaJsonData {
        return {
            id: '',
            type: 'captcha'
        };
    }

    protected override renderElement(data: CaptchaJsonData, options?: RenderOptions): HTMLElement {
        const captchaConfig = (options?.runtimeContext as any)?.captcha as CaptchaRuntimeConfig;

        if (!captchaConfig) {
            console.warn('Captcha runtime config not provided');
            const wrapper = document.createElement('div');
            wrapper.classList.add('field-wrapper', 'field-captcha', 'captcha-not-configured');
            return wrapper;
        }

        switch (captchaConfig.type) {
            case 'TruCaptcha':
                return this.renderTruCaptcha(data, captchaConfig);
            case 'ReCaptchaV2':
                return this.renderReCaptchaV2(data, captchaConfig);
            case 'ReCaptchaV3':
                return this.renderReCaptchaV3(data, captchaConfig);
            default:
                console.warn(`Unknown Captcha type: ${captchaConfig.type}`);
                return document.createElement('div');
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private renderTruCaptcha(data: CaptchaJsonData, config: CaptchaRuntimeConfig): HTMLElement {
        const container = document.createElement('div');
        container.classList.add('field-wrapper', 'field-captcha', 'tru-captcha-container');
        container.dataset['element'] = 'widget';
        container.dataset['widget'] = 'captcha';
        container.setAttribute('data-captcha-type', 'truCaptcha');

        const captchaInputId = `captcha-${this.generateId()}`;

        const labelWrapper = document.createElement('div');
        labelWrapper.classList.add('tru-captcha-label-wrapper');

        const label = document.createElement('label');
        label.htmlFor = captchaInputId;
        label.classList.add('tru-captcha-label');
        label.textContent = 'Enter text from image';

        const asterisk = document.createElement('span');
        asterisk.classList.add('tru-captcha-required-marker');
        asterisk.textContent = '*';
        label.appendChild(asterisk);

        labelWrapper.appendChild(label);

        const imageRowWrapper = document.createElement('div');
        imageRowWrapper.classList.add('tru-captcha-image-row-wrapper');

        const imageWrapper = document.createElement('div');
        imageWrapper.classList.add('tru-captcha-image-wrapper');

        const image = document.createElement('img');
        image.classList.add('tru-captcha-image');
        image.alt = 'Image with captcha';
        image.src = '';

        imageWrapper.appendChild(image);

        const refreshWrapper = document.createElement('div');
        refreshWrapper.classList.add('tru-captcha-refresh-wrapper');

        const refreshButton = document.createElement('button');
        refreshButton.classList.add('tru-captcha-refresh-icon');
        refreshButton.type = 'button';

        refreshWrapper.appendChild(refreshButton);

        imageRowWrapper.appendChild(imageWrapper);
        imageRowWrapper.appendChild(refreshWrapper);

        const inputWrapper = document.createElement('div');
        inputWrapper.classList.add('tru-captcha-input-wrapper', 'field');

        const input = document.createElement('input');
        input.type = 'text';
        input.id = captchaInputId;
        input.classList.add('tru-captcha-control', 'input', 'default');
        input.name = '__truCaptchaText';
        input.required = true;

        inputWrapper.appendChild(input);

        container.appendChild(labelWrapper);
        container.appendChild(imageRowWrapper);
        container.appendChild(inputWrapper);

        this.initializeTruCaptcha(container, image, refreshButton);

        return container;
    }

    private renderReCaptchaV2(data: CaptchaJsonData, config: CaptchaRuntimeConfig): HTMLElement {
        const container = document.createElement('div');
        container.classList.add('field-wrapper', 'field-captcha', 'recaptcha-v2-container');
        container.dataset['element'] = 'widget';
        container.dataset['widget'] = 'captcha';
        container.setAttribute('data-captcha-type', 'googleReCaptcha');

        const captchaId = this.generateId().replace(/-/g, '');
        const wrapperId = `recaptcha-${captchaId}`;

        const wrapper = document.createElement('div');
        wrapper.classList.add('recaptcha-wrapper');
        wrapper.id = wrapperId;

        container.appendChild(wrapper);

        const siteKey = config.siteKey || '';
        const theme = config.darkMode ? 'dark' : 'light';
        const showBadge = config.showBadge || false;

        container.setAttribute('data-recaptcha-site-key', siteKey);
        container.setAttribute('data-recaptcha-theme', theme);

        if (showBadge) {
            container.setAttribute('data-recaptcha-invisible', 'true');
        }

        const windowKey = `__recaptchaElement_${captchaId}`;
        (window as any)[windowKey] = wrapper;

        const callbackScript = document.createElement('script');
        callbackScript.type = 'text/javascript';
        callbackScript.classList.add('recaptcha-callback');

        callbackScript.textContent = `
            function sendRecaptchaCallback_${captchaId}() {
                const event = new CustomEvent('recaptcha-callback', { bubbles: true });
                const captchaWidget = window.__recaptchaElement_${captchaId};
                captchaWidget?.dispatchEvent(event);
            }

            var onloadCallback_${captchaId} = function() {
                if (typeof grecaptcha === 'undefined') return;
                var element = window.__recaptchaElement_${captchaId};
                if (!element) return;
                grecaptcha.render(element, {
                    'sitekey': '${siteKey}',
                    'theme': '${theme}'${showBadge ? `,
                    'callback': () => sendRecaptchaCallback_${captchaId}(),
                    'size': 'invisible',
                    'badge': 'inline'` : ''}
                });
            };

            if (typeof grecaptcha !== 'undefined' && grecaptcha.render) {
                onloadCallback_${captchaId}();
            } else {
                window.addEventListener('load', () => {
                    if (typeof grecaptcha !== 'undefined' && grecaptcha.render) {
                        onloadCallback_${captchaId}();
                    }
                });
            }
        `;

        container.appendChild(callbackScript);

        this.loadGoogleReCaptchaScript();

        return container;
    }

    private renderReCaptchaV3(data: CaptchaJsonData, config: CaptchaRuntimeConfig): HTMLElement {
        const container = document.createElement('div');
        container.classList.add('field-wrapper', 'field-captcha', 'recaptcha-v3-container');
        container.dataset['element'] = 'widget';
        container.dataset['widget'] = 'captcha';
        container.setAttribute('data-captcha-type', 'googleReCaptchaV3');

        const siteKey = config.siteKey || '';
        container.setAttribute('data-recaptcha-site-key', siteKey);

        const elementContent = document.createElement('div');
        elementContent.classList.add('element-content');
        container.appendChild(elementContent);

        this.loadGoogleReCaptchaV3Script(siteKey);

        return container;
    }

    private initializeTruCaptcha(
        container: HTMLElement,
        image: HTMLImageElement,
        refreshButton: HTMLButtonElement
    ): void {
        // Detect base URL from script src or use current origin
        const baseUrl = this.detectBaseUrl();

        const loadCaptcha = async () => {
            try {
                // POST to create new captcha challenge
                const response = await fetch(`${baseUrl}/api/public/tru-captcha`, {
                    method: 'POST'
                });
                const captchaId = await response.json();

                // Set image URL
                image.src = `${baseUrl}/api/public/tru-captcha/image/${captchaId}`;

                let captchaIdInput = container.querySelector('input[name="__truCaptchaId"]') as HTMLInputElement;
                if (!captchaIdInput) {
                    captchaIdInput = document.createElement('input');
                    captchaIdInput.type = 'hidden';
                    captchaIdInput.name = '__truCaptchaId';
                    container.appendChild(captchaIdInput);
                }
                captchaIdInput.value = captchaId;
            } catch (error) {
                console.error('Failed to load TruCaptcha:', error);
            }
        };

        (container as any).truCaptchaRefresh = loadCaptcha;

        void loadCaptcha();
        refreshButton.addEventListener('click', async () => {
            await loadCaptcha();
        });
    }

    private detectBaseUrl(): string {
        // Try to detect API URL from tru-form element's api-url attribute
        const truForm = document.querySelector('tru-form[api-url]');
        if (truForm) {
            const apiUrl = truForm.getAttribute('api-url');
            if (apiUrl) return apiUrl;
        }

        // Try to detect from script src
        const scripts = Array.from(document.querySelectorAll('script[src*="tru-forms-embed"]'));
        for (let i = 0; i < scripts.length; i++) {
            const src = scripts[i].getAttribute('src');
            if (src) {
                try {
                    const url = new URL(src, window.location.href);
                    return `${url.protocol}//${url.host}`;
                } catch {
                    // Ignore invalid URLs
                }
            }
        }

        // Fallback to current origin
        return window.location.origin;
    }

    private loadGoogleReCaptchaScript(): void {
        if (document.querySelector('script.recaptcha-js-resource')) {
            return;
        }

        const script = document.createElement('script');
        script.classList.add('recaptcha-js-resource');
        script.src = 'https://www.google.com/recaptcha/api.js?render=explicit&hl=en';
        script.async = true;
        script.defer = true;

        document.head.appendChild(script);
    }

    private loadGoogleReCaptchaV3Script(siteKey: string): void {
        if (document.querySelector('script.recaptcha-v3-js-resource')) {
            return;
        }

        const script = document.createElement('script');
        script.classList.add('recaptcha-v3-js-resource');
        script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}&hl=en`;
        script.async = true;
        script.defer = true;

        document.head.appendChild(script);
    }
}
